import { Box, Group, SimpleGrid } from '@mantine/core'
import { format } from 'date-fns'
import { AppBadge, AppButton, AppText } from '../../app/common/ui'
import { FirearmPartDto, FirearmPartFormValues } from '../../app/models/firearm'

const CONDITION_COLORS: Record<string, string> = {
  New: 'green',
  Excellent: 'green',
  Good: 'blue',
  Fair: 'amber',
  Worn: 'orange',
  'Needs Replacement': 'red'
}

function formatCurrency (value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

function getConditionColor (condition: string | null | undefined): string {
  if (!condition) return 'gray'
  return CONDITION_COLORS[condition] ?? 'gray'
}

interface PartItem {
  id?: string;
  name: string;
  description?: string | null;
  partNumber?: string | null;
  manufacturer?: string | null;
  value: number;
  condition?: string | null;
  installDate?: string | null;
  notes?: string | null;
}

interface Props {
  parts: PartItem[];
  totalValue?: number;
  editable?: boolean;
  onAdd?: () => void;
  onAddFromBin?: () => void;
  onEdit?: (part: FirearmPartFormValues, index: number) => void;
  onDelete?: (index: number) => void;
}

export default function PartsDisplay ({ parts, totalValue, editable, onAdd, onAddFromBin, onEdit, onDelete }: Props) {
  if (parts.length === 0 && !editable) {
    return null
  }

  if (parts.length === 0) {
    return (
      <Box>
        <AppText size="md" color="dimmed" align="center" py="xl">
          No parts added yet. Track scopes, magazines, and accessories to monitor total value.
        </AppText>
        {editable && (onAdd || onAddFromBin) && (
          <Group spacing="xs">
            {onAdd && (
              <AppButton onClick={onAdd} variant="light" color="gray">
                Add part
              </AppButton>
            )}
            {onAddFromBin && (
              <AppButton onClick={onAddFromBin} variant="light" color="gray">
                Add from parts bin
              </AppButton>
            )}
          </Group>
        )}
      </Box>
    )
  }

  return (
    <Box>
      {editable && (onAdd || onAddFromBin) && (
        <Group position="apart" mb="md">
          <AppText weight={600} size="sm" color="dimmed">
            Custom parts ({parts.length})
          </AppText>
          <Group spacing="xs">
            {onAdd && (
              <AppButton size="xs" variant="filled" onClick={onAdd}>
                Add part
              </AppButton>
            )}
            {onAddFromBin && (
              <AppButton size="xs" variant="light" onClick={onAddFromBin}>
                Add from parts bin
              </AppButton>
            )}
          </Group>
        </Group>
      )}
      <SimpleGrid cols={1} breakpoints={[{ minWidth: 480, cols: 2 }, { minWidth: 768, cols: 3 }]} spacing="md">
        {parts.map((part, index) => (
          <Box
            key={part.id ?? `new-${index}`}
            sx={{
              padding: 'var(--gs-md)',
              borderRadius: 8,
              background: 'var(--gs-surface)',
              border: '1px solid var(--gs-border)',
              transition: 'border-color 150ms',
              '&:hover': { borderColor: 'var(--gs-border-bright)' }
            }}
          >
            <Group position="apart" mb="var(--gs-sm)">
              <AppText weight={600} size="md" lineClamp={1}>
                {part.name}
              </AppText>
              {part.condition && (
                <AppBadge
                  size="sm"
                  color={getConditionColor(part.condition)}
                  variant="light"
                  radius="xl"
                >
                  {part.condition}
                </AppBadge>
              )}
            </Group>
            {(part.partNumber || part.manufacturer) && (
              <AppText size="xs" color="dimmed" mb={4}>
                {[part.manufacturer, part.partNumber].filter(Boolean).join(' · ')}
              </AppText>
            )}
            <AppText weight={600} size="lg" mb="var(--gs-sm)" sx={{ color: 'var(--gs-gold)', fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(part.value)}
            </AppText>
            {part.description && (
              <AppText size="sm" color="dimmed" lineClamp={2} mb={4}>
                {part.description}
              </AppText>
            )}
            {part.installDate && (
              <AppText size="xs" color="dimmed">
                Installed {format(new Date(part.installDate), 'MMM d, yyyy')}
              </AppText>
            )}
            {editable && (onEdit || onDelete) && (
              <Group spacing="xs" mt="md" sx={{ borderTop: '1px solid var(--gs-border)', paddingTop: 12 }}>
                {onEdit && (
                  <AppButton
                    size="xs"
                    variant="subtle"
                    onClick={() =>
                      onEdit(
                        {
                          id: (part as FirearmPartDto).id,
                          name: part.name,
                          description: part.description ?? '',
                          partNumber: part.partNumber ?? '',
                          manufacturer: part.manufacturer ?? '',
                          value: part.value,
                          condition: part.condition ?? '',
                          installDate: part.installDate ?? '',
                          notes: part.notes ?? ''
                        },
                        index
                      )
                    }
                  >
                    Edit
                  </AppButton>
                )}
                {onDelete && (
                  <AppButton size="xs" variant="subtle" color="red" onClick={() => onDelete(index)}>
                    Remove
                  </AppButton>
                )}
              </Group>
            )}
          </Box>
        ))}
      </SimpleGrid>
      {totalValue !== undefined && totalValue > 0 && (
        <Box
          mt="md"
          py="sm"
          px="md"
          sx={{
            borderRadius: 8,
            backgroundColor: 'rgba(0,0,0,.03)',
            border: '1px solid var(--gs-border)'
          }}
        >
          <Group position="apart">
            <AppText size="sm" weight={600} color="dimmed">
              Total parts value
            </AppText>
            <AppText weight={700} size="lg" sx={{ color: 'var(--gs-gold)', fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(totalValue)}
            </AppText>
          </Group>
        </Box>
      )}
    </Box>
  )
}
